/**
 * Repeat Product Interest — pure derivation from per-session product
 * engagement visits (`product_engagement_visits`).
 *
 * A "deep visit" is one session's engagement with one product where the
 * visitor spent ≥ 30s of ACTIVE time and reached ≥ 60% scroll depth.
 * "Repeat Product Interest" = ≥ 2 deep visits in DISTINCT sessions within
 * 14 days, for a product the visitor has not purchased.
 *
 * Everything here is pure so session separation, dedup, scoring inputs,
 * purchase suppression and consent-adjacent rules are unit-testable
 * without a database. No medical-condition inference happens anywhere in
 * this pipeline; the signal is strictly "engaged with a product page".
 */
import { isBlockingPurchaseOrder } from "./product-interest-guards";

export const DEEP_VISIT_MIN_ACTIVE_MS = 30_000;
export const DEEP_VISIT_MIN_SCROLL_PCT = 60;
export const REPEAT_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;
export const RECENT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
export const REPEAT_MIN_DEEP_VISITS = 2;

/**
 * Known product-page sections ranked shallow → deep. The client tracker
 * keeps only the highest-ranked section seen; unknown ids never emit.
 */
export const SECTION_RANK: Record<string, number> = {
  hero: 1,
  "buy-box": 2,
  benefits: 3,
  ingredients: 4,
  "how-it-works": 5,
  comparison: 6,
  reviews: 7,
  faq: 8,
};

export interface EngagementVisit {
  sessionId: string;
  productHandle: string;
  maxScrollPercent: number;
  deepestSection: string | null;
  activeTimeMs: number;
  firstSeenAt: string;
  lastSeenAt: string;
}

/** Maps a `product_engagement_visits` row to the pure shape. */
export function engagementVisitFromRow(row: {
  session_id: string;
  product_handle: string;
  max_scroll_percent: number | null;
  deepest_section: string | null;
  active_time_ms: number | null;
  first_seen_at: string;
  last_seen_at: string;
}): EngagementVisit {
  return {
    sessionId: row.session_id,
    productHandle: row.product_handle,
    maxScrollPercent: Number(row.max_scroll_percent ?? 0),
    deepestSection: row.deepest_section ?? null,
    activeTimeMs: Number(row.active_time_ms ?? 0),
    firstSeenAt: row.first_seen_at,
    lastSeenAt: row.last_seen_at,
  };
}

/** A "deep visit": ≥30s active time AND ≥60% scroll depth. */
export function isDeepVisit(
  v: Pick<EngagementVisit, "activeTimeMs" | "maxScrollPercent">,
): boolean {
  return (
    v.activeTimeMs >= DEEP_VISIT_MIN_ACTIVE_MS &&
    v.maxScrollPercent >= DEEP_VISIT_MIN_SCROLL_PCT
  );
}

/**
 * Merge two rows that share the same (visitor, session, product) dedup
 * key. Values only ever GROW: max scroll, max active time, earliest first
 * seen, latest last seen. The deepest-section label comes from the row
 * with the greater scroll depth (ties → most recent).
 */
export function mergeEngagementVisits(
  a: EngagementVisit,
  b: EngagementVisit,
): EngagementVisit {
  const deeper =
    a.maxScrollPercent !== b.maxScrollPercent
      ? a.maxScrollPercent > b.maxScrollPercent
        ? a
        : b
      : new Date(a.lastSeenAt).getTime() >= new Date(b.lastSeenAt).getTime()
        ? a
        : b;
  return {
    sessionId: a.sessionId,
    productHandle: a.productHandle,
    maxScrollPercent: Math.max(a.maxScrollPercent, b.maxScrollPercent),
    deepestSection: deeper.deepestSection ?? a.deepestSection ?? b.deepestSection,
    activeTimeMs: Math.max(a.activeTimeMs, b.activeTimeMs),
    firstSeenAt:
      new Date(a.firstSeenAt).getTime() <= new Date(b.firstSeenAt).getTime()
        ? a.firstSeenAt
        : b.firstSeenAt,
    lastSeenAt:
      new Date(a.lastSeenAt).getTime() >= new Date(b.lastSeenAt).getTime()
        ? a.lastSeenAt
        : b.lastSeenAt,
  };
}

/**
 * Collapse duplicate rows for the same (session, product). The DB unique
 * index already enforces this; the client-side pass is a safety net for
 * reads that span backfills.
 */
export function dedupeVisits(visits: readonly EngagementVisit[]): EngagementVisit[] {
  const byKey = new Map<string, EngagementVisit>();
  for (const v of visits) {
    const key = `${v.sessionId}::${v.productHandle}`;
    const cur = byKey.get(key);
    byKey.set(key, cur ? mergeEngagementVisits(cur, v) : v);
  }
  return [...byKey.values()];
}

export interface ProductInterestSummary {
  handle: string;
  /** Deep visits in DISTINCT sessions within the 14d window. */
  deepVisits14d: number;
  /** Subset of the above whose last activity was within 7 days. */
  deepVisits7d: number;
  lastVisitAt: string | null;
  maxDepthPct: number;
  deepestSection: string | null;
  hasCart: boolean;
  purchased: boolean;
  /** ≥2 deep visits in distinct sessions within 14d AND not purchased. */
  isRepeat: boolean;
}

export interface SummarizeOpts {
  now: Date;
  /**
   * Earliest reliable identification timestamp. Visits whose last activity
   * predates identification are excluded (same post-identification rule
   * as the event-based classifier). `null` disables the exclusion.
   */
  identifiedAt?: string | null;
  purchasedHandles?: ReadonlySet<string>;
  cartHandles?: ReadonlySet<string>;
  windowMs?: number;
}

/**
 * Build per-product interest summaries from raw engagement visits.
 * Returned summaries are sorted by most recent deep visit first.
 */
export function summarizeRepeatInterest(
  visits: readonly EngagementVisit[],
  opts: SummarizeOpts,
): ProductInterestSummary[] {
  const nowMs = opts.now.getTime();
  const idMs = opts.identifiedAt ? new Date(opts.identifiedAt).getTime() : null;
  if (opts.identifiedAt && (idMs === null || !Number.isFinite(idMs))) return [];
  const windowStart = nowMs - (opts.windowMs ?? REPEAT_WINDOW_MS);
  const recentStart = nowMs - RECENT_WINDOW_MS;

  const byProduct = new Map<string, EngagementVisit[]>();
  for (const v of dedupeVisits(visits)) {
    if (!isDeepVisit(v)) continue;
    const lastMs = new Date(v.lastSeenAt).getTime();
    if (!Number.isFinite(lastMs)) continue;
    if (idMs !== null && lastMs < idMs) continue; // pre-identification
    if (lastMs < windowStart) continue;
    const list = byProduct.get(v.productHandle) ?? [];
    list.push(v);
    byProduct.set(v.productHandle, list);
  }

  const out: ProductInterestSummary[] = [];
  for (const [handle, list] of byProduct) {
    // dedupeVisits guarantees one row per (session, product) — each entry
    // here is a DISTINCT session.
    const deepVisits14d = list.length;
    const deepVisits7d = list.filter(
      (v) => new Date(v.lastSeenAt).getTime() >= recentStart,
    ).length;
    const byRecency = [...list].sort(
      (a, b) => new Date(a.lastSeenAt).getTime() - new Date(b.lastSeenAt).getTime(),
    );
    const last = byRecency[byRecency.length - 1];
    const deepest = [...list].sort(
      (a, b) => b.maxScrollPercent - a.maxScrollPercent,
    )[0];
    const purchased = opts.purchasedHandles?.has(handle) ?? false;
    out.push({
      handle,
      deepVisits14d,
      deepVisits7d,
      lastVisitAt: last?.lastSeenAt ?? null,
      maxDepthPct: deepest?.maxScrollPercent ?? 0,
      deepestSection: deepest?.deepestSection ?? null,
      hasCart: opts.cartHandles?.has(handle) ?? false,
      purchased,
      isRepeat: deepVisits14d >= REPEAT_MIN_DEEP_VISITS && !purchased,
    });
  }
  out.sort(
    (a, b) =>
      new Date(b.lastVisitAt ?? 0).getTime() -
      new Date(a.lastVisitAt ?? 0).getTime(),
  );
  return out;
}

/**
 * The top repeat-interest product (most recent deep visit first), or null
 * when nothing qualifies. Purchased products never qualify.
 */
export function pickTopRepeatInterest(
  summaries: readonly ProductInterestSummary[],
): ProductInterestSummary | null {
  return summaries.find((s) => s.isRepeat) ?? null;
}

/**
 * From order items with their parent order's status fields inlined,
 * returns the set of product handles that count as PURCHASED (paid /
 * captured / partially refunded). Cancelled, voided and fully-refunded
 * orders do not suppress. Reuses the shared blocking-order rule.
 */
export function purchasedHandlesFromOrderItems(
  items: ReadonlyArray<{
    handle: string | null | undefined;
    status?: string | null;
    financial_status?: string | null;
  }>,
): Set<string> {
  // Local import-free re-implementation is avoided on purpose — keep the
  // single shared rule in product-interest-guards.
  const out = new Set<string>();
  for (const it of items) {
    if (!it.handle) continue;
    if (isBlockingPurchaseOrder(it)) out.add(it.handle);
  }
  return out;
}

// ─────────────────────── Session grouping (activity drawer) ─────────────────────

export interface SessionActivityEvent {
  sessionId: string | null;
  type: string;
  route: string | null;
  at: string;
}

export interface SessionEngagement {
  handle: string;
  maxScrollPercent: number;
  activeTimeMs: number;
  deepestSection: string | null;
  deep: boolean;
}

export interface SessionGroup {
  sessionId: string;
  startedAt: string;
  lastAt: string;
  /** Distinct page paths in chronological order (capped). */
  pages: string[];
  eventCount: number;
  engagement: SessionEngagement[];
}

/**
 * Group flat customer events into per-session activity blocks for the
 * admin drawer, attaching any product engagement visits captured for each
 * session. Sorted most-recent first.
 */
export function groupEventsIntoSessions(
  events: readonly SessionActivityEvent[],
  visits: readonly EngagementVisit[],
  opts: { maxSessions?: number; maxPages?: number } = {},
): SessionGroup[] {
  const maxSessions = opts.maxSessions ?? 15;
  const maxPages = opts.maxPages ?? 6;
  const visitsBySession = new Map<string, SessionEngagement[]>();
  for (const v of dedupeVisits(visits)) {
    const list = visitsBySession.get(v.sessionId) ?? [];
    list.push({
      handle: v.productHandle,
      maxScrollPercent: v.maxScrollPercent,
      activeTimeMs: v.activeTimeMs,
      deepestSection: v.deepestSection,
      deep: isDeepVisit(v),
    });
    visitsBySession.set(v.sessionId, list);
  }

  const groups = new Map<string, SessionGroup & { pageSet: Set<string> }>();
  for (const e of events) {
    if (!e.sessionId) continue;
    let g = groups.get(e.sessionId);
    if (!g) {
      g = {
        sessionId: e.sessionId,
        startedAt: e.at,
        lastAt: e.at,
        pages: [],
        pageSet: new Set<string>(),
        eventCount: 0,
        engagement: [],
      };
      groups.set(e.sessionId, g);
    }
    if (e.at < g.startedAt) g.startedAt = e.at;
    if (e.at > g.lastAt) g.lastAt = e.at;
    g.eventCount += 1;
    if (e.route && !g.pageSet.has(e.route)) {
      g.pageSet.add(e.route);
      if (g.pages.length < maxPages) g.pages.push(e.route);
    }
  }

  const out: SessionGroup[] = [...groups.values()].map(({ pageSet: _p, ...g }) => ({
    ...g,
    engagement: (visitsBySession.get(g.sessionId) ?? []).sort(
      (a, b) => b.activeTimeMs - a.activeTimeMs,
    ),
  }));
  out.sort((a, b) => +new Date(b.lastAt) - +new Date(a.lastAt));
  return out.slice(0, maxSessions);
}
